/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useState } from 'react';
import { BarChart3, ClipboardCheck, ThumbsUp } from 'lucide-react';
import { api, type Analytics as AnalyticsData } from '../../lib/api';
import { approvalCardViews, approvalHeadline, APPROVAL_UNDECIDED_LABEL, type ApprovalCardView } from '../../lib/approvalCards';
import { EmptyState, ErrorState, Grid, Kpi, Panel, Select, Stack, Table, Tag, type Column } from '../../ui';
import { count, money, percent, ratio } from '../../ui/format';

/**
 * Analytics › Cost: the token and cost views the Usage page held before the usage analytics (overhaul step 4): usage,
 * cost, quality, and tool and skill ROI rolled up from per-turn traces (GET /v1/admin/analytics). The same call as before,
 * its own window (the traces' 7, 30 or 90 days; the usage lens does not apply to spend), money in the one format, numbers
 * right-aligned. Nothing of the old page is lost: it is this tab.
 */
export function UsageCost() {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  // Try again after a failed load: the same window, read again.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api.analytics(days).then(setData).catch((e: unknown) => setError(e)).finally(() => setLoading(false));
  }, [days, attempt]);

  const appColumns: Array<Column<AnalyticsData['perApp'][number]>> = [
    { key: 'app', header: 'App', cell: (a) => <Tag>{a.app}</Tag> },
    { key: 'requests', header: 'Requests', numeric: true, cell: (a) => count(a.requests) },
    { key: 'cost', header: 'Cost', numeric: true, cell: (a) => money(a.costUsd) },
    { key: 'errors', header: 'Errors', numeric: true, cell: (a) => count(a.errors) },
    { key: 'ms', header: 'Avg ms', numeric: true, cell: (a) => count(a.avgMs) },
  ];
  const toolColumns: Array<Column<AnalyticsData['perTool'][number]>> = [
    { key: 'tool', header: 'Tool', cell: (t) => <Tag>{t.tool}</Tag> },
    { key: 'calls', header: 'Calls', numeric: true, cell: (t) => count(t.calls) },
    { key: 'proposed', header: 'Proposed', numeric: true, cell: (t) => count(t.proposed) },
    { key: 'auto', header: 'Auto', numeric: true, cell: (t) => count(t.autoExecuted) },
    { key: 'blocked', header: 'Blocked', numeric: true, hideOnPhone: true, cell: (t) => count(t.blocked) },
    { key: 'denied', header: 'Denied', numeric: true, hideOnPhone: true, cell: (t) => count(t.denied) },
    { key: 'errors', header: 'Err', numeric: true, cell: (t) => count(t.errors) },
  ];
  const skillColumns: Array<Column<AnalyticsData['perSkill'][number]>> = [
    { key: 'skill', header: 'Skill', cell: (s) => <Tag>{s.id}</Tag> },
    { key: 'retrievals', header: 'Retrievals', numeric: true, cell: (s) => count(s.retrievals) },
    { key: 'score', header: 'Avg score', numeric: true, cell: (s) => s.avgScore },
  ];
  const cardColumns: Array<Column<ApprovalCardView>> = [
    {
      key: 'tool', header: 'Tool / system',
      cell: (a) => (
        <span className="ui-cell-stack">
          <span className="ui-row-tags"><Tag>{a.tool}</Tag><Tag>{a.app}</Tag></span>
          {a.note ? <span className="ui-cell-sub">{a.note}</span> : null}
        </span>
      ),
    },
    { key: 'proposed', header: 'Proposed', numeric: true, cell: (a) => count(a.proposed) },
    { key: 'confirmed', header: 'Confirmed', numeric: true, cell: (a) => count(a.confirmed) },
    { key: 'cancelled', header: 'Cancelled', numeric: true, cell: (a) => count(a.cancelled) },
    { key: 'rejected', header: 'Rejected', numeric: true, cell: (a) => count(a.rejected) },
    { key: 'awaiting', header: 'Awaiting approver', numeric: true, cell: (a) => count(a.awaiting) },
    { key: 'undecided', header: APPROVAL_UNDECIDED_LABEL, numeric: true, cell: (a) => count(a.undecided) },
    { key: 'share', header: 'Confirmed share', numeric: true, cell: (a) => (a.confirmedShare === null ? '—' : `${a.confirmedShare}%`) },
    { key: 'median', header: 'Median time to decide', numeric: true, cell: (a) => a.medianText },
  ];
  const userColumns: Array<Column<AnalyticsData['topUsers'][number]>> = [
    { key: 'user', header: 'User (hashed)', mono: true, cell: (u) => u.userHash },
    { key: 'requests', header: 'Requests', numeric: true, cell: (u) => count(u.requests) },
    { key: 'cost', header: 'Cost', numeric: true, cell: (u) => money(u.costUsd) },
  ];
  const cards = approvalCardViews(data?.approvals);

  return (
    <Stack>
      <div className="ui-toolbar">
        <span className="ui-cell-sub">Usage, cost, quality, and tool and skill ROI rolled up from per-turn traces</span>
        <Select size="sm" aria-label="Cost window" className="ui-w-sm" value={days} onChange={(e) => setDays(Number(e.target.value))}>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </Select>
      </div>
      {error ? (
          <ErrorState title="Usage could not be loaded" error={error} onRetry={() => setAttempt((n) => n + 1)} retrying={loading} />
        ) : (
          <Stack>
            <Grid cols={4} label="Totals">
              <Kpi label="Requests" loading={loading} value={data ? count(data.totals.requests) : null} sub={`last ${days} days`} />
              <Kpi label="Est. cost" loading={loading} value={data ? money(data.totals.costUsd) : null} sub="Anthropic list pricing" />
              <Kpi label="Errors" loading={loading} value={data ? count(data.totals.errors) : null} sub={data ? percent(ratio(data.totals.errors, data.totals.requests), 1) : null} />
              <Kpi label="Avg latency" loading={loading} value={data ? `${count(data.totals.avgMs)} ms` : null} sub="per turn" />
            </Grid>

            <Grid cols={2}>
              <Panel flush title={<span className="ui-title-icon"><BarChart3 size={16} strokeWidth={1.75} aria-hidden="true" />Cost by app</span>}>
                <Table stacked label="Cost by app" columns={appColumns} rows={data?.perApp ?? []} rowKey={(a) => a.app} loading={loading} skeletonRows={4} stickyHeader={false} empty={<EmptyState compact icon={BarChart3} title="No data">Nothing in this window.</EmptyState>} />
              </Panel>
              <Stack>
                <Panel title={<span className="ui-title-icon"><ThumbsUp size={16} strokeWidth={1.75} aria-hidden="true" />Answer quality</span>} description="Ratings people gave answers">
                  <dl className="ui-facts">
                    <dt>Rated</dt><dd>{data ? count(data.quality.rated) : '—'}</dd>
                    <dt>Up</dt><dd>{data ? count(data.quality.up) : '—'}</dd>
                    <dt>Down</dt><dd>{data ? count(data.quality.down) : '—'}</dd>
                    <dt>CSAT</dt><dd>{data?.quality.rated ? percent(ratio(data.quality.up, data.quality.rated)) : '—'}</dd>
                  </dl>
                </Panel>
                <Panel flush title="Skill ROI">
                  <Table stacked label="Skill ROI" columns={skillColumns} rows={data?.perSkill ?? []} rowKey={(s) => s.id} loading={loading} skeletonRows={4} stickyHeader={false} empty={<EmptyState compact icon={BarChart3} title="No skills retrieved yet">Skills that never fire show as absent here.</EmptyState>} />
                </Panel>
              </Stack>
            </Grid>

            <Panel flush title="Tool usage" description="Calls by tool: how many ran on their own, waited for a confirmation, were blocked or denied, or failed">
              <Table stacked label="Tool usage" columns={toolColumns} rows={data?.perTool ?? []} rowKey={(t) => t.tool} loading={loading} skeletonRows={4} stickyHeader={false} empty={<EmptyState compact icon={BarChart3} title="No tool calls yet">Tool calls appear here once Gyro makes them.</EmptyState>} />
            </Panel>

            <Panel
              flush
              title={<span className="ui-title-icon"><ClipboardCheck size={16} strokeWidth={1.75} aria-hidden="true" />Approval cards</span>}
              description={data ? approvalHeadline(data.approvals, days) : undefined}
              footer={(
                <span>
                  Each card counts once, by where it stands now. A card with no decision is still open, expired unanswered or ended by a tool setting.
                  Time to decide is the person&apos;s own decision (confirm, send for approval or cancel) from the proposal; an approver&apos;s later decision is not timed.
                </span>
              )}
            >
              <Table stacked className="ui-table--wrap" label="Approval cards" columns={cardColumns} rows={cards} rowKey={(a) => `${a.app}:${a.tool}`} loading={loading} skeletonRows={3} stickyHeader={false} empty={<EmptyState compact icon={ClipboardCheck} title="No approval cards in this window">Cards appear here once Gyro proposes actions.</EmptyState>} />
            </Panel>

            <Panel flush title="Top users by spend" description="Chargeback: people by their hashed id">
              <Table stacked label="Top users by spend" columns={userColumns} rows={data?.topUsers ?? []} rowKey={(u) => u.userHash} loading={loading} skeletonRows={4} stickyHeader={false} empty={<EmptyState compact icon={BarChart3} title="No data">Nothing in this window.</EmptyState>} />
            </Panel>
          </Stack>
        )}
    </Stack>
  );
}
